import { VuexModule, Module, Action, Mutation, getModule } from 'vuex-module-decorators';
import store from '@/store';
import {
  setAgentId,
  getAgentId,
  removeAgentId,
  setRefreshToken,
  getRefreshToken,
  removeRefreshToken,
  setExpiresIn,
  getExpiresIn,
  removeExpiresIn,
  setLoginTime,
  getLoginTime,
  removeLoginTime,
  getStaffId,
  setStaffId,
  removeApplyTipsDialog
} from '@/utils/storage_session';
import { setToken, removeToken, getToken } from '@/utils/cookie_auth';
import { getUserApi, loginApi, logoutApi, refreshTokenApi, getIssueRootApi } from '@/api/login';
import router, { resetRouter } from '@/router';
import { ILogin } from '@/interface/login';
import { PermissionModule } from '@/store/modules/permission';

// 声明user模块的state变量类型
export interface IUserState {
  fkAgentId: number | string;
  token: string;
  refreshToken: string;
  expiresIn: string;
  loginTime: string;
  loginId: string;
  name: string;
  nameEn: string;
  nickname: string;
  companyId: number;
  studentIdIssue: number | string;
  fkPlatformType: string;
  currentPlatform: string;
  privateFilesUrl: string;
  publicFilesUrl: string;
  agentCompanyName: string;
  rolesDatas: any[];
  indexSelectYear: string;
  showIndexTabs: boolean;
  bmsLoginId: string;
  pageIndexSelectYear: string;
  staffId: string;
  bmsLogin: boolean;
  resourceKeys: string[];
  issueInnerAccount: boolean;
  personInfo: any;
  onlineAgentUserId: string;
  subFkPlatformType: string;
}

/**
 * 通过命名空间的形式来使用module，这里的namespaced值即为user
 * store 为主入口页对应的整个vuex模块的store
 */
@Module({ dynamic: true, store, name: 'user'})
class User extends VuexModule implements IUserState {
  public fkAgentId = getAgentId();
  public token = getToken();
  public refreshToken = getRefreshToken();
  public expiresIn = getExpiresIn();
  public loginTime = getLoginTime();
  public loginId = '';
  public name = '';
  public nameEn = '';
  public nickname = '';
  public companyId = -1;
  public studentIdIssue = -1;
  public fkPlatformType = '';
  public currentPlatform = '';
  public privateFilesUrl = '';
  public publicFilesUrl = '';
  public agentCompanyName = '';
  public rolesDatas = [];
  public indexSelectYear = new Date().getFullYear() + '';
  public showIndexTabs = true;
  public bmsLoginId = '';
  public pageIndexSelectYear = new Date().getFullYear() + '';
  public staffId = getStaffId();
  public bmsLogin = false;
  public resourceKeys: string[] = [];
  public issueInnerAccount = false;
  public personInfo = {}
  public onlineAgentUserId='';
  public subFkPlatformType='';

  get getToken() {
    return this.token;
  }

  // 一旦使用@Mutation装饰某一函数后, 函数内的this上下文即指向当前的state
  @Mutation
  public SET_TOKEN_INFO(tokenInfo: any) {
    this.token = tokenInfo.access_token;
    this.expiresIn = tokenInfo.expires_in;
    this.loginTime = tokenInfo.login_time;
    this.staffId = tokenInfo.staff_id;
    setToken(this.token);
    setExpiresIn(this.expiresIn);
    setLoginTime(this.loginTime);
    setStaffId(this.staffId)
  }

  // 设置权限模块
  @Mutation
  public SET_ROLES(roles: string[]) {
    this.resourceKeys = roles;
  }

  @Mutation
  public SET_REFRESH_TOKEN(refreshToken: string) {
    this.refreshToken = refreshToken;
    setRefreshToken(this.refreshToken);
  }

  @Mutation
  public SET_NAME(name: string) {
    this.name = name;
  }
  @Mutation
  public SET_LOGINID(loginId: string) {
    this.loginId = loginId;
  }
  @Mutation
  public SET_NAMEEN(nameEn: string) {
    this.nameEn = nameEn;
  }

  @Mutation
  public SET_NICKNAME(nickname: string) {
    this.nickname = nickname;
  }

  @Mutation
  public SET_AGENTID(agentId: string) {
    this.fkAgentId = agentId;
    setAgentId(agentId)
  }

  @Mutation
  public SET_STUDENT_ID_ISSUE(studentIdIssue: number) {
    this.studentIdIssue = studentIdIssue;
  }

  @Mutation
  public SET_PLATFORMTYPE(platformType: string) {
    this.fkPlatformType = platformType;
  }

  @Mutation
  public SET_CUR_PLATFORMTYPE(currentPlatform: string) {
    this.currentPlatform = currentPlatform;
  }

  @Mutation
  public SET_PRIVATE_FILES_URL(privateFilesUrl: string) {
    this.privateFilesUrl = privateFilesUrl;
  }

  @Mutation
  public SET_PUBLIC_FILES_URL(publicFilesUrl: string) {
    this.publicFilesUrl = publicFilesUrl;
  }

  @Mutation
  public SET_AGENT_COMPANY_NAME(company: string) {
    this.agentCompanyName = company;
  }

  @Mutation
  public SET_BMS_LOGIN_ID(bmsLoginId: string) {
    this.bmsLoginId = bmsLoginId;
  }

  @Mutation
  public SET_PAGE_INDEX_SELECT_YEAR(year: string) {
    this.pageIndexSelectYear = year;
  }

  @Mutation
  public SET_BMS_LOGIN(bmsLogin: boolean) {
    this.bmsLogin = bmsLogin;
  }

  @Mutation
  public SET_ISSUE_INNER_ACCOUNT(issueInnerAccount: boolean) {
    this.issueInnerAccount = issueInnerAccount;
  }

  @Mutation
  public SET_COMPANY_ID(companyId: string) {
    this.companyId = parseInt(companyId, 10);
  }
  @Mutation
  public SET_ISSUE_ROOT(issueRoot: any) {
    this.rolesDatas = issueRoot;
  }

  // 首页申请总览，选择的年份
  @Mutation
  public SET_INDEX_SELECT_YEAR(indexSelectYear: string) {
    this.indexSelectYear = indexSelectYear;
  }

  @Mutation
  public SET_INDEX_SHOW_TABS(showIndexTabs: boolean) {
    this.showIndexTabs = showIndexTabs;
  }
  @Mutation
  public ONLINE_AGENT_USER_ID(onlineAgentUserId: string) {
    this.onlineAgentUserId = onlineAgentUserId;
  }
  @Mutation
  public SUB_FK_PLAT_FORM_TYPE(subFkPlatformType: string) {
    this.subFkPlatformType = subFkPlatformType;
  }
  @Action
  public setIndexTabsShow(showIndexTabs: boolean) {
    this.SET_INDEX_SHOW_TABS(showIndexTabs);
  }

  @Action
  public ResetToken() {
    this.SET_REFRESH_TOKEN('');
    removeToken();
    removeExpiresIn();
    removeLoginTime();
    removeRefreshToken();
    removeApplyTipsDialog();
    this.SET_ROLES([]);
    this.SET_TOKEN_INFO({
      expires_in: '',
      login_time: '',
      access_token: '',
      staff_id: ''
    });
  }

  @Action
  public ResetAgentId() {
    this.SET_AGENTID('');
    removeAgentId();
  }

  @Action
  public GetUser() {
    return new Promise((resolve, rejects) => {
      getUserApi().then(({ data }) => {
        const user = data.data;
        this.SET_NAME(user.name);
        this.SET_LOGINID(user.loginId);
        this.SET_NAMEEN(user.nameEn);
        this.SET_NICKNAME(user.nickname);
        this.SET_AGENTID(user.fkAgentId);
        this.SET_COMPANY_ID(user.company);
        this.SET_BMS_LOGIN(user.bmsLogin);
        this.SET_ISSUE_INNER_ACCOUNT(user.issueInnerAccount);
        this.SET_PLATFORMTYPE(user.fkPlatformType);
        this.SET_PUBLIC_FILES_URL(user.publicFilesUrl);
        this.SET_STUDENT_ID_ISSUE(user.studentIdIssue);
        this.SET_CUR_PLATFORMTYPE(user.currentPlatform);
        this.SET_PRIVATE_FILES_URL(user.privateFilesUrl);
        this.SET_AGENT_COMPANY_NAME(user.agentCompanyName);
        this.SET_BMS_LOGIN_ID(user.bmsLoginId);
        this.SET_ROLES(user.resourceKeys);
        this.ONLINE_AGENT_USER_ID(user.onlineAgentUserId);
        this.SUB_FK_PLAT_FORM_TYPE(user.subFkPlatformType);
        // 暂时取消内部账号路由权限判断
        // if (user.issueInnerAccount) {
        //   PermissionModule.GenerateRoutes(user.resourceKeys);
        //   router.addRoutes(PermissionModule.dynamicRoutes);
        // }
        resolve('');
      }).catch(() => {
        this.ResetToken();
        router.push('/login');
        rejects('');
      });
    })
  }
  @Action
  public async GetIssueRoot() {
    try {
      const { data } = await getIssueRootApi();
      if (data.datas && data.datas.length) {
        this.SET_ISSUE_ROOT(data.datas)
      }
    } catch (err) {
      //
    }
  }

  @Action
  public async RefreshToken() {
    try {
      const { data }: any = await refreshTokenApi(this.refreshToken);
      this.SET_TOKEN_INFO(data);
    } catch (err) {
      return Promise.reject();
    }
  }

  @Action
  public Login(
    userObj: ILogin
  ) {
    const params: any = {
      ...userObj,
      grant_type: 'password',
      requestUrlType: location.host,
      fkPlatformType: 'get_issue'
    }
    if (userObj.currentTimeMillis) {
      params.type = 'avatarByKey';
    }
    return new Promise((resolve, reject) => {
      loginApi(params).then(({ data }) => {
        this.SET_TOKEN_INFO(data);
        this.SET_REFRESH_TOKEN(data.refresh_token);
        resolve(data);
      }).catch((err) => {
        console.log(err);
        // 当用户没有邀请码10086，或邀请码无效时10087，拦截登录，需填入正确的邀请码才可登录
        if (['10086', '10087', '10089', '10090', '10096'].includes(err.error_description)) {
          resolve(err.error_description);
        } else {
          if (err.error_description === '10091') {
            reject(err.error_description);
          } else{
            reject();
          }
        }
      })
    })
  }

  @Action
  public async LogOut() {
    try {
      await logoutApi();
      this.SET_NAME('');
      this.SET_NAMEEN('');
      this.SET_NAMEEN('');
      this.SET_NICKNAME('');
      this.SET_AGENTID('');
      this.SET_BMS_LOGIN(false);
      this.SET_ISSUE_INNER_ACCOUNT(false);
      this.SET_STUDENT_ID_ISSUE(-1);
      this.SET_CUR_PLATFORMTYPE('');
      this.SET_PLATFORMTYPE('');
      this.ResetToken();
      this.ResetAgentId();
      this.SET_ISSUE_ROOT([])
      resetRouter();
    } catch (err) {
      return Promise.reject();
    }
  }
}

export const UserModule = getModule(User);
